import React, { useState, useEffect, useCallback } from 'react';
import { getActivites, createActivite, getClients, getProduits, getCommerciaux } from '../services/api';
import {
  FiPlus, FiSearch, FiFilter, FiMapPin, FiImage, FiFileText, FiPhone,
  FiUsers, FiTruck, FiCreditCard, FiChevronDown, FiChevronUp, FiCalendar,
  FiClock, FiDollarSign, FiDownload, FiEye, FiMessageSquare, FiRefreshCw,
  FiChevronLeft, FiChevronRight, FiX
} from 'react-icons/fi';
import SearchableSelect from '../components/SearchableSelect';

const TYPES = ['visite', 'appel', 'reunion', 'prospection', 'livraison', 'recouvrement', 'vente', 'commande', 'installation', 'sensibilisation', 'inscription', 'recrutement'];
const TYPE_COLORS: Record<string, string> = {
  visite: '#16A34A', appel: '#3B82F6', reunion: '#8B5CF6', prospection: '#F59E0B',
  livraison: '#06B6D4', recouvrement: '#EF4444', vente: '#16A34A', commande: '#3B82F6',
  installation: '#F97316', sensibilisation: '#14B8A6', inscription: '#F59E0B', recrutement: '#EF4444'
};
const TYPE_ICONS: Record<string, React.ReactNode> = {
  visite: <FiMapPin size={16} />, appel: <FiPhone size={16} />, reunion: <FiUsers size={16} />,
  prospection: <FiSearch size={16} />, livraison: <FiTruck size={16} />, recouvrement: <FiCreditCard size={16} />,
  vente: <FiDollarSign size={16} />, commande: <FiFileText size={16} />, installation: <FiTruck size={16} />,
  sensibilisation: <FiUsers size={16} />, inscription: <FiFileText size={16} />, recrutement: <FiUsers size={16} />,
};
const STATUS_COLORS: Record<string, string> = {
  planifiee: '#94A3B8', en_cours: '#3B82F6', terminee: '#16A34A', valide: '#8B5CF6', annulee: '#EF4444'
};
const STATUS_LABELS: Record<string, string> = {
  planifiee: 'Planifiee', en_cours: 'En cours', terminee: 'Terminee', valide: 'Validee', annulee: 'Annulee'
};

const formatMoney = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const Activites: React.FC = () => {
  const [activites, setActivites] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [commercialFilter, setCommercialFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [produits, setProduits] = useState<any[]>([]);
  const [commerciaux, setCommerciaux] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<any>({
    client_id: '', produit_id: '', type: 'visite', description: '',
    quantite: 1, montant: 0, notes: '', latitude: '', longitude: ''
  });

  const limit = 20;
  const hasActiveFilters = typeFilter || statutFilter || searchText || dateDebut || dateFin || commercialFilter || clientFilter;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit };
      if (typeFilter) params.type = typeFilter;
      if (statutFilter) params.statut = statutFilter;
      if (searchText) params.search = searchText;
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      if (commercialFilter) params.user_id = commercialFilter;
      if (clientFilter) params.client_id = clientFilter;
      const res = await getActivites(params);
      setActivites(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch { /* */ }
    setLoading(false);
  }, [page, typeFilter, statutFilter, searchText, dateDebut, dateFin, commercialFilter, clientFilter]);

  useEffect(() => {
    Promise.all([
      getClients({ limit: 500 }).then(res => setClients(res.data.data || [])),
      getProduits().then(res => setProduits(res.data.data || res.data || [])),
      getCommerciaux().then(res => setCommerciaux(res.data.data || res.data || [])).catch(() => {}),
    ]);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Stats
  const totalMontant = activites.reduce((s: number, a: any) => s + (Number(a.montant) || 0), 0);
  const nbTerminees = activites.filter((a: any) => a.statut === 'terminee' || a.statut === 'valide').length;
  const nbTypes = new Set(activites.map((a: any) => a.type)).size;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      ...form, client_id: form.client_id || null, produit_id: form.produit_id || null,
      horodatage: new Date().toISOString(), latitude: form.latitude || null, longitude: form.longitude || null
    };
    await createActivite(data);
    setShowModal(false);
    loadData();
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        setForm({ ...form, latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      });
    }
  };

  const openCreate = () => {
    setForm({ client_id: '', produit_id: '', type: 'visite', description: '', quantite: 1, montant: 0, notes: '', latitude: '', longitude: '' });
    getLocation();
    setShowModal(true);
  };

  const clearFilters = () => {
    setTypeFilter(''); setStatutFilter(''); setSearchText('');
    setDateDebut(''); setDateFin(''); setCommercialFilter(''); setClientFilter('');
    setPage(1);
  };

  const exportCSV = () => {
    const headers = ['Date', 'Type', 'Statut', 'Commercial', 'Client', 'Description', 'Montant', 'Quantite'];
    const rows = activites.map((a: any) => [
      new Date(a.horodatage || a.date_activite).toLocaleString('fr-FR'),
      a.type, STATUS_LABELS[a.statut] || a.statut, a.user_nom || '',
      a.client_nom || '', (a.description || '').replace(/;/g, ','),
      Math.round(Number(a.montant) || 0), a.quantite || 1
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activites_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="page-activites" style={{ padding: 0 }}>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Activites Terrain</h2>
          <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: 14 }}>{total} activite{total > 1 ? 's' : ''} trouvee{total > 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiDownload size={16} /> Export CSV
          </button>
          <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiPlus size={16} /> Nouvelle activite
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        {[
          { label: 'Total activites', value: activites.length, icon: <FiFileText />, color: '#3B82F6', bg: '#EFF6FF' },
          { label: 'Terminees/Validees', value: nbTerminees, icon: <FiClock />, color: '#16A34A', bg: '#F0FDF4' },
          { label: 'Montant total', value: `${formatMoney(totalMontant)} F`, icon: <FiDollarSign />, color: '#F59E0B', bg: '#FFF7ED' },
          { label: 'Types differents', value: nbTypes, icon: <FiUsers />, color: '#8B5CF6', bg: '#F5F3FF' },
        ].map((kpi, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 'var(--r-md)', padding: '18px 20px',
            boxShadow: 'var(--sh-card)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 16
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12, background: kpi.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color, fontSize: 22
            }}>{kpi.icon}</div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1E293B' }}>{kpi.value}</div>
              <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>{kpi.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search + Filters bar */}
      <div style={{
        background: '#fff', borderRadius: 'var(--r-md)', padding: '16px 20px', marginBottom: 16,
        boxShadow: 'var(--sh-card)', border: '1px solid var(--border)'
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: showFilters ? 16 : 0 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <FiSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input
              type="text" placeholder="Rechercher par client, commercial, description..."
              value={searchText} onChange={e => { setSearchText(e.target.value); setPage(1); }}
              style={{
                width: '100%', padding: '10px 14px 10px 42px', border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)', fontSize: 14
              }}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
              borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
              background: hasActiveFilters ? '#16A34A' : '#fff',
              color: hasActiveFilters ? '#fff' : '#64748B', fontWeight: 600, fontSize: 13, cursor: 'pointer'
            }}
          >
            <FiFilter size={16} /> Filtres {hasActiveFilters && `(actifs)`}
          </button>
          <button onClick={() => { loadData(); }} style={{
            padding: '10px 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
            background: '#fff', color: '#64748B', cursor: 'pointer'
          }}>
            <FiRefreshCw size={16} />
          </button>
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Commercial</label>
                <SearchableSelect
                  value={commercialFilter}
                  onChange={(v: string) => { setCommercialFilter(v); setPage(1); }}
                  placeholder="Tous les commerciaux"
                  options={commerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.matricule || '' }))}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Client</label>
                <SearchableSelect
                  value={clientFilter}
                  onChange={(v: string) => { setClientFilter(v); setPage(1); }}
                  placeholder="Tous les clients"
                  options={clients.map((c: any) => ({ value: String(c.id), label: c.raison_sociale, sub: c.nom_contact || c.zone || '' }))}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Type</label>
                <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 13 }}>
                  <option value="">Tous les types</option>
                  {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, alignItems: 'end' }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Statut</label>
                <select value={statutFilter} onChange={e => { setStatutFilter(e.target.value); setPage(1); }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 13 }}>
                  <option value="">Tous</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Date debut</label>
                  <input type="date" value={dateDebut} onChange={e => { setDateDebut(e.target.value); setPage(1); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Date fin</label>
                  <input type="date" value={dateFin} onChange={e => { setDateFin(e.target.value); setPage(1); }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 13 }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {hasActiveFilters && (
                  <button onClick={clearFilters} style={{
                    padding: '8px 14px', borderRadius: 'var(--r-sm)', border: 'none',
                    background: '#FEE2E2', color: '#EF4444', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4
                  }}>
                    <FiX size={14} /> Effacer les filtres
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Type filter pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          onClick={() => { setTypeFilter(''); setPage(1); }}
          style={{
            padding: '6px 16px', borderRadius: 20, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            background: !typeFilter ? '#1E293B' : '#F1F5F9', color: !typeFilter ? '#fff' : '#64748B'
          }}>Toutes</button>
        {TYPES.filter(t => activites.some((a: any) => a.type === t) || typeFilter === t).map(t => {
          const cnt = activites.filter((a: any) => a.type === t).length;
          const isActive = typeFilter === t;
          return (
            <button key={t} onClick={() => { setTypeFilter(isActive ? '' : t); setPage(1); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                borderRadius: 20, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: isActive ? TYPE_COLORS[t] : `${TYPE_COLORS[t]}15`,
                color: isActive ? '#fff' : TYPE_COLORS[t]
              }}>
              {TYPE_ICONS[t]} {t.charAt(0).toUpperCase() + t.slice(1)}
              {cnt > 0 && <span style={{ background: isActive ? 'rgba(255,255,255,0.3)' : `${TYPE_COLORS[t]}20`, borderRadius: 10, padding: '0px 6px', fontSize: 11 }}>{cnt}</span>}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-card)',
        border: '1px solid var(--border)', overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            Chargement...
          </div>
        ) : activites.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>
            <FiClock size={40} style={{ marginBottom: 12 }} />
            <p style={{ fontWeight: 600 }}>Aucune activite trouvee</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} style={{
                marginTop: 10, padding: '8px 20px', borderRadius: 8, border: 'none',
                background: '#16A34A', color: '#fff', fontWeight: 600, cursor: 'pointer'
              }}>Effacer les filtres</button>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Commercial</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Client</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Date</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Statut</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Montant</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748B' }}></th>
              </tr>
            </thead>
            <tbody>
              {activites.map((a: any) => {
                const color = TYPE_COLORS[a.type] || '#94A3B8';
                const stColor = STATUS_COLORS[a.statut] || '#94A3B8';
                const isExpanded = expandedId === a.id;
                return (
                  <React.Fragment key={a.id}>
                    <tr
                      onClick={() => setExpandedId(isExpanded ? null : a.id)}
                      style={{
                        borderBottom: isExpanded ? 'none' : '1px solid #F1F5F9',
                        cursor: 'pointer', transition: 'background 0.15s',
                        background: isExpanded ? '#FAFBFC' : '#fff'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#F8FAFC')}
                      onMouseLeave={e => (e.currentTarget.style.background = isExpanded ? '#FAFBFC' : '#fff')}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 10, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            background: `${color}15`, color
                          }}>{TYPE_ICONS[a.type] || <FiFileText size={16} />}</div>
                          <span style={{ fontWeight: 600, fontSize: 14, textTransform: 'capitalize' }}>{a.type}</span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600 }}>{a.user_nom || '--'}</td>
                      <td style={{ padding: '14px 16px', fontSize: 14, color: '#374151' }}>{a.client_nom || '--'}</td>
                      <td style={{ padding: '14px 16px', fontSize: 13, color: '#64748B' }}>
                        {new Date(a.horodatage || a.date_activite).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: `${stColor}15`, color: stColor, textTransform: 'uppercase'
                        }}>
                          {STATUS_LABELS[a.statut] || a.statut}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                        {Number(a.montant) > 0 ? `${formatMoney(a.montant)} F` : '--'}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#94A3B8' }}>
                        {isExpanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, borderBottom: '1px solid #E2E8F0' }}>
                          <div style={{
                            padding: '16px 20px', background: '#FAFBFC',
                            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16
                          }}>
                            {/* Details */}
                            <div>
                              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiEye size={14} /> Details
                              </h4>
                              <div style={{ display: 'grid', gap: 8 }}>
                                {a.produit_nom && (
                                  <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #F1F5F9' }}>
                                    <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>Produit</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{a.produit_nom}</div>
                                  </div>
                                )}
                                {a.quantite > 1 && (
                                  <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #F1F5F9' }}>
                                    <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>Quantite</div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{a.quantite}</div>
                                  </div>
                                )}
                                {a.latitude && (
                                  <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #F1F5F9' }}>
                                    <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <FiMapPin size={11} /> Localisation GPS
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 500 }}>
                                      {Number(a.latitude).toFixed(5)}, {Number(a.longitude).toFixed(5)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Description / Notes */}
                            <div>
                              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiMessageSquare size={14} /> Notes & Description
                              </h4>
                              {a.description && (
                                <div style={{
                                  padding: '10px 14px', background: '#fff', borderRadius: 8,
                                  border: '1px solid #F1F5F9', marginBottom: 8
                                }}>
                                  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: '#374151' }}>{a.description}</p>
                                </div>
                              )}
                              {a.notes && (
                                <div style={{
                                  padding: '10px 14px', background: '#FFF7ED', borderRadius: 8,
                                  border: '1px solid #FDE68A'
                                }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E', marginBottom: 4 }}>Notes</div>
                                  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: '#78350F' }}>{a.notes}</p>
                                </div>
                              )}
                              {!a.description && !a.notes && (
                                <p style={{ color: '#94A3B8', fontSize: 13, fontStyle: 'italic' }}>Aucune note</p>
                              )}
                            </div>

                            {/* Media */}
                            <div>
                              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiImage size={14} /> Medias
                              </h4>
                              {a.photos && a.photos.length > 0 ? (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {a.photos.map((p: any) => (
                                    <img key={p.id} src={p.url} alt={p.legende || ''} style={{
                                      width: 80, height: 80, borderRadius: 8, objectFit: 'cover',
                                      border: '1px solid #E2E8F0'
                                    }} />
                                  ))}
                                </div>
                              ) : (
                                <p style={{ color: '#94A3B8', fontSize: 13, fontStyle: 'italic' }}>Aucun media</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 20px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC'
          }}>
            <span style={{ fontSize: 13, color: '#64748B' }}>
              Page {page} sur {totalPages} ({total} resultats)
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{
                padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)',
                background: '#fff', cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600
              }}>
                <FiChevronLeft size={16} /> Precedent
              </button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{
                padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)',
                background: '#fff', cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600
              }}>
                Suivant <FiChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <h3 style={{ margin: '0 0 20px' }}>Nouvelle Activite</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Type *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {TYPES.map(t => (
                    <button key={t} type="button"
                      onClick={() => setForm({ ...form, type: t })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                        borderRadius: 10, border: form.type === t ? `2px solid ${TYPE_COLORS[t]}` : '1px solid #E2E8F0',
                        background: form.type === t ? `${TYPE_COLORS[t]}15` : '#fff',
                        color: form.type === t ? TYPE_COLORS[t] : '#64748B',
                        fontWeight: 600, fontSize: 13, cursor: 'pointer'
                      }}>
                      {TYPE_ICONS[t]} {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Client</label>
                  <SearchableSelect
                    value={form.client_id}
                    onChange={(v: string) => setForm({ ...form, client_id: v })}
                    placeholder="Rechercher un client..."
                    options={clients.map((c: any) => ({ value: String(c.id), label: c.raison_sociale, sub: c.nom_contact || '' }))}
                  />
                </div>
                <div className="form-group">
                  <label>Produit</label>
                  <select value={form.produit_id} onChange={e => setForm({ ...form, produit_id: e.target.value })}>
                    <option value="">--</option>
                    {produits.map((p: any) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Quantite</label>
                  <input type="number" value={form.quantite} onChange={e => setForm({ ...form, quantite: Number(e.target.value) })} min={1} />
                </div>
                <div className="form-group">
                  <label>Montant (F)</label>
                  <input type="number" value={form.montant} onChange={e => setForm({ ...form, montant: Number(e.target.value) })} />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
              <div className="form-row geo-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748B' }}>
                  <FiMapPin /> {form.latitude ? `${Number(form.latitude).toFixed(4)}, ${Number(form.longitude).toFixed(4)}` : 'Non localise'}
                </span>
                <button type="button" className="btn btn-sm btn-secondary" onClick={getLocation}>Localiser</button>
              </div>
              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Activites;
